/**
 * Slice B read surfaces — live adapter, React Query hooks, and the canonical
 * org-id seam. Pure types / mappers / mock adapter live in ./sliceB-core (kept
 * import-free of Supabase + react-query so they are node-testable).
 *
 * Edge contract (verified from committed source @ 0512f28):
 *   Request  { surface, canonical_org_id, practice (warm_path only), as_of? }
 *   Success  { data: <projection jsonb unmodified> }
 *   Errors   400 { error: { code: "bad_request", message } }
 *            401 { error: { code, message } }   (code passed through VERBATIM)
 *
 * LIVE WIRING IS HELD (owner ruling 2026-08-11): the edge contract requires a
 * `canonical_org_id`, and no client-callable path exposes it to a regular user
 * today (the signal carries only the physical related_organization_id; the
 * resolver `resolve_organization` is super_admin-only). Canonical sourcing is
 * isolated behind the single seam `useCanonicalOrgId` below — mock returns a
 * stable per-signal key; live returns null until a backend path lands (serialize
 * canonical on get_signal_detail, or open the resolver's authz). The Live adapter
 * is complete and dormant so the live swap is a one-line seam change.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import { ADAPTER_MODE } from "./adapter-context";
import type { FeedSignal } from "./types";
import {
  createMockSliceBAdapter,
  mapCompositeInputs,
  mapRkgModal,
  mapWarmPath,
  toSliceBError,
  type CompositeInputs,
  type RawCompositeInputs,
  type RawRkgModal,
  type RawWarmPath,
  type RkgModal,
  type SliceBReadAdapter,
  type WarmPath,
} from "./sliceB-core";

// Re-export the core surface so callers keep a single import site (../../sliceB).
export * from "./sliceB-core";

/* ============================================================
   Live adapter (DORMANT until canonical sourcing lands)
   ============================================================ */

type Surface = "warm_path" | "rkg_modal" | "composite_inputs";

async function invokeSliceB<T>(surface: Surface, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke("get_slice_b_surfaces", { body: { surface, ...body } }),
    `get_slice_b_surfaces:${surface}`,
    SUPABASE_EDGE_TIMEOUT_MS,
  );
  if (error) throw await toSliceBError(error);
  // Contract: success is single-nested { data: <projection jsonb unmodified> }.
  return (data as { data: T }).data;
}

class LiveSliceBAdapter implements SliceBReadAdapter {
  async getWarmPath(input: { canonicalOrgId: string; practice: string }): Promise<WarmPath> {
    const raw = await invokeSliceB<RawWarmPath>("warm_path", {
      canonical_org_id: input.canonicalOrgId,
      practice: input.practice,
    });
    return mapWarmPath(raw);
  }
  async getRkgModal(input: { canonicalOrgId: string; asOf?: string }): Promise<RkgModal> {
    const raw = await invokeSliceB<RawRkgModal>("rkg_modal", {
      canonical_org_id: input.canonicalOrgId,
      ...(input.asOf ? { as_of: input.asOf } : {}),
    });
    return mapRkgModal(raw);
  }
  async getCompositeInputs(input: { canonicalOrgId: string; asOf?: string }): Promise<CompositeInputs> {
    const raw = await invokeSliceB<RawCompositeInputs>("composite_inputs", {
      canonical_org_id: input.canonicalOrgId,
      ...(input.asOf ? { as_of: input.asOf } : {}),
    });
    return mapCompositeInputs(raw);
  }
}

/* ============================================================
   Selection + the canonical-org-id seam + hooks
   ============================================================ */

let adapterSingleton: SliceBReadAdapter | null = null;
export function sliceBAdapter(): SliceBReadAdapter {
  if (!adapterSingleton) {
    adapterSingleton = ADAPTER_MODE === "live" ? new LiveSliceBAdapter() : createMockSliceBAdapter();
  }
  return adapterSingleton;
}

/**
 * THE canonical-org-id seam. Live wiring landed 2026-08-11: get_signal_detail v14
 * serves `canonical_organization_id` (resolved via v_organization_canonical_resolution,
 * blue-proven incl. the member-org round trip), surfaced on FeedSignal.canonicalOrgId.
 * - live: use the detail's canonical id; while the modal is painted from the feed-cache
 *   row (no canonical id yet) or if a payload predates v14, status is "unavailable" and
 *   the surfaces show an honest empty state — never a call with a wrong (physical) id.
 * - mock: a stable per-signal key.
 */
export function useCanonicalOrgId(signal: FeedSignal): {
  canonicalOrgId: string | null;
  status: "mock" | "resolved" | "unavailable";
} {
  if (ADAPTER_MODE === "live") {
    const id = signal.canonicalOrgId ?? null;
    return { canonicalOrgId: id, status: id ? "resolved" : "unavailable" };
  }
  return { canonicalOrgId: signal.id, status: "mock" };
}

const STALE_MS = 60_000;

export function useSliceBWarmPath(
  canonicalOrgId: string | null,
  practice: string,
): UseQueryResult<WarmPath> {
  const governedPractice = practice.trim();
  return useQuery({
    queryKey: ["slice-b", "warm_path", canonicalOrgId, governedPractice],
    enabled: Boolean(canonicalOrgId) && governedPractice.length > 0,
    staleTime: STALE_MS,
    retry: false,
    queryFn: () =>
      sliceBAdapter().getWarmPath({
        canonicalOrgId: canonicalOrgId as string,
        practice: governedPractice,
      }),
  });
}

export function useSliceBRkgModal(
  canonicalOrgId: string | null,
  enabled = true,
): UseQueryResult<RkgModal> {
  return useQuery({
    queryKey: ["slice-b", "rkg_modal", canonicalOrgId],
    enabled: enabled && Boolean(canonicalOrgId),
    staleTime: STALE_MS,
    retry: false,
    queryFn: () => sliceBAdapter().getRkgModal({ canonicalOrgId: canonicalOrgId as string }),
  });
}

export function useSliceBCompositeInputs(
  canonicalOrgId: string | null,
): UseQueryResult<CompositeInputs> {
  return useQuery({
    queryKey: ["slice-b", "composite_inputs", canonicalOrgId],
    enabled: Boolean(canonicalOrgId),
    staleTime: STALE_MS,
    retry: false,
    queryFn: () => sliceBAdapter().getCompositeInputs({ canonicalOrgId: canonicalOrgId as string }),
  });
}
